import React from 'react';
import 'react-dates-21/initialize';
import TimeZones from 'legacy/time_zones';
import moment from 'moment-timezone';
import type { Moment } from 'moment';

import { standardRequestWithErrorFlash } from 'shared/utils/request';
import { navigateTo } from 'shared/utils/navigation';
import {
  findPronunciationAudio,
  findPronounValues,
} from 'candidate_availability/form_utils';
import type {
  CandidatePronouns,
  CandidatePronunciation,
  TimeZoneOption,
  Slot,
  SlotsByDay,
} from '../../types';
import type { Props as FooterProps } from './footer';
import type { BonsaiConfiguration } from '@grnhse/bonsai/lib/core';

export type Props = {
  start: string;
  end: string;
  availableSlots: Array<Slot>;
  candidateName: string;
  interviewLocation: string;
  interviewLength: number;
  jobTitle: string;
  interviewName: string;
  interviewInvitation: string;
  schedulePath: string;
  useVideo: boolean;
  videoConferencingProvider: string | null | undefined;
  timeZones: Array<TimeZoneOption>;
  isMobile: boolean;
  footer: FooterProps;
  candidatePronunciation: CandidatePronunciation;
  candidatePronouns: CandidatePronouns;
  isReschedule: boolean;
  bonsaiEnabled: boolean;
  bonsaiProps: BonsaiConfiguration | null;
  logoUrl: string | null;
};

export type State = {
  selectedTimeZoneOption: TimeZoneOption;
  selectedDate: Moment | null | undefined;
  selectedTime: Slot | null | undefined;
  currentMonth: Moment;
  submitting: boolean;
  currentScreen?: number | null | undefined;
  prevScreen?: number | null | undefined;
  page?: number;
  availableSlotsByDay: SlotsByDay;
};

const userTimeZone = TimeZones.detect();
const fallBackTimeZone = {
  label: '(GMT+00:00) UTC',
  value: 'UTC',
  ianaTimeZoneId: 'Etc/UTC',
} as const;

export default class CandidateViewShell extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      selectedTimeZoneOption:
        props.timeZones.find((tz) => tz.ianaTimeZoneId === userTimeZone) ||
        fallBackTimeZone,
      selectedDate: null,
      selectedTime: null,
      currentMonth: moment(props.start),
      submitting: false,
      availableSlotsByDay: {},
    };
  }

  componentDidMount() {
    this.groupSlotsByDay();
  }

  dateForTime = (time: string) => {
    return moment(time)
      .tz(this.state.selectedTimeZoneOption.ianaTimeZoneId)
      .format('YYYY-MM-DD');
  };

  groupSlotsByDay = () => {
    const groupedSlots: Record<string, any> = {};
    this.props.availableSlots.forEach((slot) => {
      const date = this.dateForTime(slot.start);
      groupedSlots[date] = groupedSlots[date] || [];
      groupedSlots[date].push(slot);
    });
    this.setState({ availableSlotsByDay: groupedSlots });
  };

  handleTimeZoneChange = (timeZoneOption: TimeZoneOption) => {
    this.setState({
      selectedTimeZoneOption: timeZoneOption,
      selectedDate: null,
      selectedTime: null,
    });
    this.groupSlotsByDay();
  };

  submit = () => {
    const { schedulePath } = this.props;
    const { selectedTime, selectedTimeZoneOption } = this.state;

    if (!selectedTime) {
      return;
    }

    const payload = {
      interview_time: selectedTime.start,
      selected_timezone: selectedTimeZoneOption.ianaTimeZoneId,
      pronunciation: findPronunciationAudio(),
      pronouns: findPronounValues(),
    } as const;

    this.setState({ submitting: true });
    standardRequestWithErrorFlash
      .post(schedulePath, payload)
      .then(() => {
        navigateTo(schedulePath);
      })
      .catch(() => {
        this.setState({ submitting: false });
      });
  };
}
